import { TileTap } from '@minigame-race/contracts';
import { useEffect, useRef, useState } from 'react';
import type { MinigameProps } from '../../race/types.js';
import { color } from '../../theme.js';

/**
 * Tile Tap.
 *
 * OPTIMISTIC + RECONCILE. On pointerdown we immediately paint the tap — the
 * tapped tile goes blank, the predicted next target lights up — and only then
 * emit. The server's authoritative view lands ~1 RTT later and replaces our
 * prediction. If the event was rejected, the prediction is discarded and we snap
 * back to truth.
 *
 * We predict only the *visual*. Score and time are never computed here: the
 * `scoreMs` shown always comes from the server.
 */

interface Optimistic {
  /** Cell we predicted would go active. Cleared when the server confirms. */
  activeCell: number | null;
  cleared: number;
}

function isTileView(v: unknown): v is TileTap.TileTapPrivateView {
  return typeof v === 'object' && v !== null && (v as { kind?: string }).kind === 'tile-tap';
}

export function TileTapGame({ view, emit, rejectNonce }: MinigameProps) {
  const server = isTileView(view) ? view : null;

  const [optimistic, setOptimistic] = useState<Optimistic | null>(null);
  const [missFlash, setMissFlash] = useState<number | null>(null);
  const [hitFlash, setHitFlash] = useState<number | null>(null);
  const lastReject = useRef(rejectNonce);

  // Any rejection invalidates our prediction: fall back to server truth.
  useEffect(() => {
    if (rejectNonce !== lastReject.current) {
      lastReject.current = rejectNonce;
      setOptimistic(null);
    }
  }, [rejectNonce]);

  // Once the server's `cleared` catches up to our prediction, drop it.
  useEffect(() => {
    if (server && optimistic && server.cleared >= optimistic.cleared) setOptimistic(null);
  }, [server, optimistic]);

  if (!server) {
    return <div style={{ color: color.textFaint, fontSize: 14 }}>Loading board…</div>;
  }

  // The prediction wins until the server confirms it.
  const activeCell = optimistic?.activeCell ?? server.activeCell;
  const cleared = optimistic ? Math.max(optimistic.cleared, server.cleared) : server.cleared;

  const onTap = (cell: number): void => {
    if (server.isComplete || activeCell === null) return;

    if (cell === activeCell) {
      // Correct tap. Paint it instantly: blank this tile, and mark the target
      // "consumed" so it stops glowing. We do NOT guess where the next target
      // goes — that comes from the seeded sequence, which only the server knows.
      setOptimistic({ activeCell: null, cleared: cleared + 1 });
      setHitFlash(cell);
      window.setTimeout(() => setHitFlash(null), 140);
    } else {
      // A miss costs time server-side. Show it immediately; nothing to predict.
      setMissFlash(cell);
      window.setTimeout(() => setMissFlash(null), 260);
    }

    emit({ type: 'tap', cell } satisfies TileTap.TileTapEvent);
  };

  const combo = server.combo;

  return (
    <div
      style={{
        width: 'min(86cqw,50cqh)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'clamp(9px,1.8cqmin,20px)',
      }}
    >
      {/* header: title + combo pill */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span
            style={{
              fontWeight: 700,
              fontSize: 'clamp(15px,3cqmin,27px)',
              letterSpacing: '.04em',
            }}
          >
            TILE TAP
          </span>
          <span
            style={{
              fontSize: 'clamp(7px,1.3cqmin,11px)',
              letterSpacing: '.24em',
              fontWeight: 600,
              color: 'rgba(166,255,46,.6)',
            }}
          >
            SPEED + PRECISION
          </span>
        </div>

        {/* Cosmetic only: a readout of consecutive clean hits. Score is
            unaffected — it is always elapsed + misses * penalty. */}
        {combo >= 2 ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'clamp(5px,1.1cqmin,9px)',
              padding: 'clamp(4px,.9cqmin,8px) clamp(9px,1.8cqmin,16px)',
              border: '1px solid rgba(166,255,46,.4)',
              borderRadius: 999,
              background: 'rgba(166,255,46,.08)',
            }}
          >
            <span
              style={{
                fontSize: 'clamp(7px,1.3cqmin,11px)',
                letterSpacing: '.2em',
                fontWeight: 600,
                color: 'rgba(166,255,46,.75)',
              }}
            >
              COMBO
            </span>
            <span
              style={{
                fontFamily: "'JetBrains Mono',monospace",
                fontWeight: 800,
                fontSize: 'clamp(15px,3.2cqmin,26px)',
                lineHeight: 1,
                color: color.limeBright,
                textShadow: '0 0 clamp(5px,1.6cqmin,16px) rgba(166,255,46,.6)',
              }}
            >
              ×{combo}
            </span>
          </div>
        ) : server.misses > 0 && combo === 0 ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: 'clamp(4px,.9cqmin,8px) clamp(9px,1.8cqmin,16px)',
              border: '1px solid rgba(255,46,77,.5)',
              borderRadius: 999,
              background: 'rgba(255,46,77,.12)',
              animation: 'rhTagPulse .62s ease-in-out infinite',
            }}
          >
            <span
              style={{
                fontSize: 'clamp(7px,1.3cqmin,11px)',
                letterSpacing: '.2em',
                fontWeight: 700,
                color: color.redBright,
              }}
            >
              STREAK LOST
            </span>
          </div>
        ) : (
          <div
            style={{
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: 'clamp(10px,2cqmin,16px)',
              color: 'rgba(234,255,234,.45)',
              fontWeight: 700,
            }}
          >
            {cleared}/{server.total}
          </div>
        )}
      </div>

      {/* the 4x4 board */}
      <div
        role="grid"
        aria-label="Tile Tap board"
        style={{
          width: '100%',
          aspectRatio: '1',
          display: 'grid',
          gridTemplateColumns: 'repeat(4,1fr)',
          gridTemplateRows: 'repeat(4,1fr)',
          gap: 'clamp(5px,1.4cqmin,14px)',
          padding: 'clamp(6px,1.4cqmin,14px)',
          border: '1px solid rgba(166,255,46,.14)',
          borderRadius: 'clamp(8px,1.8cqmin,18px)',
          background: 'rgba(0,0,0,.28)',
          boxShadow: 'inset 0 0 clamp(20px,5cqmin,60px) rgba(0,0,0,.5)',
          touchAction: 'manipulation',
        }}
      >
        {Array.from({ length: TileTap.TILE_COUNT }, (_, cell) => {
          const isActive = cell === activeCell;
          const isMiss = cell === missFlash;
          const isHit = cell === hitFlash;

          return (
            <button
              key={cell}
              // pointerdown, not click: click waits for pointerup, which on a
              // tapping speed game is a perceptible tax.
              onPointerDown={(e) => {
                e.preventDefault();
                onTap(cell);
              }}
              aria-label={`Tile ${cell + 1}${isActive ? ' (active)' : ''}`}
              style={{
                position: 'relative',
                borderRadius: 'clamp(4px,1cqmin,10px)',
                border: isActive
                  ? '1px solid #d8ff8f'
                  : isMiss
                    ? '1px solid rgba(255,46,77,.7)'
                    : '1px solid rgba(166,255,46,.13)',
                background: isActive
                  ? `linear-gradient(150deg,${color.limeBright},${color.limeDeep})`
                  : isHit
                    ? 'rgba(166,255,46,.22)'
                    : 'rgba(166,255,46,.04)',
                boxShadow: isActive
                  ? '0 0 clamp(12px,3.2cqmin,34px) rgba(166,255,46,.7), inset 0 0 clamp(5px,1.4cqmin,14px) rgba(255,255,255,.55)'
                  : undefined,
                animation: isActive
                  ? 'rhActive .9s ease-in-out infinite'
                  : isMiss
                    ? 'tileMiss .26s ease-out'
                    : undefined,
                transition: isActive ? undefined : 'background .12s ease-out',
                padding: 0,
              }}
            >
              {isActive && (
                <span
                  aria-hidden
                  style={{
                    position: 'absolute',
                    inset: '30%',
                    border: 'clamp(2px,.5cqmin,4px) solid rgba(7,11,7,.65)',
                    borderRadius: '26%',
                  }}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* misses readout — the penalty is real, so surface it */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span
          style={{
            fontSize: 'clamp(7px,1.3cqmin,11px)',
            letterSpacing: '.2em',
            fontWeight: 600,
            color: 'rgba(234,255,234,.4)',
          }}
        >
          CLEARED {cleared} / {server.total}
        </span>
        {server.misses > 0 && (
          <span
            style={{
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: 'clamp(9px,1.7cqmin,14px)',
              fontWeight: 700,
              color: color.redBright,
            }}
          >
            {server.misses} MISS{server.misses === 1 ? '' : 'ES'}
          </span>
        )}
      </div>
    </div>
  );
}
